import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { toast } from 'react-toastify';

import { Context } from '../../AuthContext';

import { useCategory } from '../../CategoryContext';

import { Button, TextField } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Badge from '@mui/material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';

import Spinner from '../../components/Spinner';
import FormDialog from '../../components/FormDialog';
import ReviewNewResourcesModal from '../../components/ReviewNewResourcesModal';
import VerticalResourceSearchModal from '../../components/VerticalResourceSearchModal';
import RefreshFiltersModal from '../../components/RefreshFiltersModal';
import WatchGuideLink from '../../components/WatchGuideLink/WatchGuideLink';

import styles from './HomePage.module.css';
import { displayTime } from '../../utils/functions';
import { getPlanTier } from '../../config/planLimits';

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
});

const HomePage = () => {
  const { isEditor, isAdvancedEditor, contextLoading, verticals, refreshVerticals } = useCategory();

  const { vertical_id } = useParams();

  const [newItems, setNewItems] = useState();
  const [loading, setLoading] = useState(true);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [abstract, setAbstract] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchDialogBusy, setSearchDialogBusy] = useState(false);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);

  const { user } = useContext(Context);
  const navigate = useNavigate();
  const { subscriptionStatus } = useOutletContext() || {};
  const planTier = getPlanTier(subscriptionStatus);

  const [refreshState, setRefreshState] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const startEditingName = () => {
    setNameDraft(selectedVertical?.query || '');
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const saveVerticalName = async () => {
    const newName = nameDraft.trim();
    if (!newName || newName === selectedVertical?.query) {
      cancelEditingName();
      return;
    }
    try {
      setSavingName(true);
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}edit_vertical_name?vertical_id=${vertical_id}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: newName }),
        }
      );
      if (!res.ok) throw new Error('Failed to update name');
      setSelectedVertical((prev) => ({ ...prev, query: newName }));
      refreshVerticals?.();
      setEditingName(false);
    } catch (err) {
      console.error('Failed to rename vertical', err);
      toast.error('Failed to rename vertical.', { className: 'toast' });
    } finally {
      setSavingName(false);
    }
  };

  const downloadVerticalCsv = async () => {
    if (!user || !vertical_id) return;
    setDownloading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}download_vertical?vertical_id=${vertical_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Network response was not ok');
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${selectedVertical?.query || 'vertical'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download vertical.', { className: 'toast' });
    } finally {
      setDownloading(false);
    }
  };

  const fetchRefreshState = async () => {
    if (!user?.uid) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}refresh_state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setRefreshState(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch refresh state', err);
    }
  };

  useEffect(() => {
    if (!user?.uid || !isAdvancedEditor) return;
    fetchRefreshState();
  }, [user?.uid, isAdvancedEditor]);

  const refreshDisabled = refreshState && refreshState.used >= refreshState.limit;

  const editSelectedVertical = ({ arg, value }) => {
    setSelectedVertical((prev) => ({
      ...prev,
      [arg]: value,
    }));
  };

  const getItemsForReview = async (token) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}get_items_to_review?vertical_id=${vertical_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 403) {
        //navigate('/forbidden', { replace: true });
        return null;
      }
      const data = await res.json();
      return data;
    } catch (error) {
      console.error('Error fetching new items:', error);
    }
  };

  const searchNewContent = async (selectedCategories, filterOverrides = {}) => {
    try {
      setSearchDialogBusy(true);
      setSearchDialogOpen(false);
      setFiltersDialogOpen(false);
      setSelectedVertical((prev) => ({
        ...prev,
        status: 'Updating',
        reviewed: false,
        ...filterOverrides,
      }));

      const { members_count, user_id, updated_at, created_at, ...payload } = {
        ...selectedVertical,
        ...filterOverrides,
        status: 'Updating',
        reviewed: false,
        selected_categories: selectedCategories,
      };
      const token = await user.getIdToken();

      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}fetchNewContent?vertical_id=${vertical_id}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (res.status === 403) {
        let body = {};
        try {
          body = await res.json();
        } catch (_) {
          // ignore
        }
        if (body && body.code === 'refresh_limit_reached') {
          toast.error(
            "You've reached your Vertical Refresh limit. Additional refreshes become available after your allowance resets.",
            { className: 'toast', autoClose: 5000 }
          );
          // Roll back the optimistic Updating status and re-sync authoritative state.
          setSelectedVertical((prev) => (prev ? { ...prev, status: 'Completed' } : prev));
          fetchRefreshState();
          return;
        }
        navigate('/forbidden', { replace: true });
        return;
      }

      // Optimistic local decrement; reload re-fetches authoritative state.
      setRefreshState((prev) => (prev ? { ...prev, used: prev.used + 1 } : prev));

      setShouldPoll(true);
    } catch (error) {
      console.error('Error fetching vertical details:', error);
    } finally {
      setSearchDialogBusy(false);
      setLoading(false);
    }
  };

  const handleCategoriesSelected = (selectedCategories) => {
    const needsFunding = selectedCategories.includes('funding');
    const needsRegion = ['organizations', 'jobs', 'events'].some((c) =>
      selectedCategories.includes(c)
    );
    if (needsFunding || needsRegion) {
      setPendingCategories(selectedCategories);
      setSearchDialogOpen(false);
      setFiltersDialogOpen(true);
    } else {
      searchNewContent(selectedCategories);
    }
  };

  const toggleAutoRefresh = async (checked) => {
    if (!user) return;
    const token = await user.getIdToken();
    setSelectedVertical((prev) => ({
      ...prev,
      auto_refresh: checked,
    }));

    const res = await fetch(
      `${process.env.REACT_APP_FLASK_WEBSERVER}toggle_auto_refresh?vertical_id=${vertical_id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          auto_refresh: checked,
        }),
      }
    );
  };

  useEffect(() => {
    document.title = 'Home - The Simple Platform';

    setLoading(true);
    const fetchVerticalDetails = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}get_vertical_details?vertical_id=${vertical_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.status === 403) {
          navigate('/forbidden', { replace: true });
          return;
        }
        const data = await res.json();
        setSelectedVertical(data);
        setAbstract(data?.abstract || '');

        if (data?.status === 'Updating') {
          setShouldPoll(true);
        }

        const data_ = await getItemsForReview(token);
        if (!data_) return;
        setNewItems(data_);
      } catch (error) {
        console.error('Error fetching vertical details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!contextLoading && user) {
      fetchVerticalDetails();
    }
  }, [vertical_id, contextLoading, user, navigate]);

  useEffect(() => {
    if (!shouldPoll) return;

    let intervalId;

    const fetchStatus = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}task?id=${vertical_id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();

        if (data?.status === 'Completed') {
          const data_ = await getItemsForReview(token);
          if (!data_) return;
          const count = Object.values(data_ ?? {}).reduce((sum, arr) => sum + arr.length, 0);
          if (count === 0) {
            toast.info(
              'No new items found. Refresh the vertical after a few days to discover the latest content',
              {
                className: 'toast',
                autoClose: 8000,
              }
            );
          }

          setNewItems(data_);
          clearInterval(intervalId);
          intervalId = null;
          setShouldPoll(false);
        }

        setSelectedVertical((prev) => ({
          ...prev,
          status: data?.status,
          reviewed: data?.reviewed,
          updated_at: data?.updated_at,
        }));
      } catch (err) {
        console.error(err);
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    fetchStatus();
    intervalId = setInterval(fetchStatus, 10000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [vertical_id, shouldPoll, user]);

  const totalMembers = selectedVertical?.members_count || 0;

  const currentVertical = verticals?.find((v) => v.id === vertical_id);
  const permissionLabel =
    currentVertical?.status === 'Created'
      ? 'Owner'
      : currentVertical?.permission === 'AdvancedEditor'
      ? 'Advanced Editor'
      : currentVertical?.permission === 'Editor'
      ? 'Basic Editor'
      : currentVertical
      ? 'Member'
      : '';

  const countNewItems = Object.values(newItems ?? {}).reduce((sum, arr) => sum + arr.length, 0);

  const showBadge = selectedVertical?.status === 'Completed' && countNewItems > 0;

  return loading ? (
    <Spinner />
  ) : (
    <div style={{ width: '75%' }} className="papers">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 className={styles.title}>Welcome back, {user.displayName} &#128075;!</h3>
        <div style={{ margin: '0 0 0 auto' }}>
          <WatchGuideLink
            videoId="understanding-the-workspace"
            label="Watch Guide: Understanding the Vertical Workspace"
          />
        </div>
      </div>

      <div className={styles.section} style={{ marginTop: 0 }}>
        {selectedVertical && (
          <>
            <div className={styles.verticalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 className={styles.verticalTitle}>Vertical:</h3>
                {editingName ? (
                  <>
                    <TextField
                      autoFocus
                      size="small"
                      variant="outlined"
                      value={nameDraft}
                      disabled={savingName}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveVerticalName();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEditingName();
                        }
                      }}
                      inputProps={{ maxLength: 200 }}
                      sx={{ minWidth: '260px' }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={savingName || !nameDraft.trim()}
                      onClick={saveVerticalName}
                      sx={{ textTransform: 'none' }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={savingName}
                      onClick={cancelEditingName}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className={styles.verticalTitle}>{selectedVertical.query}</h3>
                    {isAdvancedEditor && (
                      <img
                        src={`${process.env.PUBLIC_URL}/pen.svg`}
                        alt="edit"
                        className="detail-icon clickable"
                        style={{ border: 'none' }}
                        onClick={startEditingName}
                      />
                    )}
                  </>
                )}
              </div>
              <p style={{ fontWeight: 600, margin: '0 0 8px 0', padding: 0 }}>
                Your Permission: {permissionLabel}
              </p>
            </div>
            {isAdvancedEditor && (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <NoMaxWidthTooltip
                      title="Trigger a fresh search for new resources relevant to this Vertical."
                      placement="top-start"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -10],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <span>
                        <Button
                          disabled={
                            selectedVertical?.status === 'Updating' ||
                            (selectedVertical?.reviewed === false && countNewItems > 0) ||
                            refreshDisabled
                          }
                          variant="contained"
                          startIcon={
                            <RefreshIcon
                              sx={{
                                animation:
                                  selectedVertical?.status === 'Updating'
                                    ? 'spin 1s linear infinite'
                                    : 'none',
                                '@keyframes spin': {
                                  from: { transform: 'rotate(0deg)' },
                                  to: { transform: 'rotate(360deg)' },
                                },
                              }}
                            />
                          }
                          sx={{ textTransform: 'none' }}
                          onClick={() => setSearchDialogOpen(true)}
                        >
                          Check for New Vertical Resources
                        </Button>
                      </span>
                    </NoMaxWidthTooltip>

                    <span style={{ fontSize: '12px', paddingTop: '4px' }}>
                      Last Run: {displayTime(selectedVertical?.updated_at)}
                    </span>
                  </div>

                  <span style={{ paddingLeft: '12px', marginTop: '8px' }}>
                    <WatchGuideLink
                      videoId="finding-new-resources"
                      label="Watch Guide: Finding New Resources"
                    />
                  </span>

                  <Badge
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    badgeContent={showBadge ? countNewItems : 0}
                    max={9999}
                    style={{ marginLeft: 'auto' }}
                    slotProps={{
                      badge: {
                        sx: {
                          backgroundColor: 'red',
                          color: 'white',
                          top: '',
                          border: '1px solid red',
                          fontSize: '12px',
                          fontWeight: '500',
                        },
                      },
                    }}
                  >
                    <span>
                      <Button
                        disabled={
                          selectedVertical?.status !== 'Completed' ||
                          selectedVertical?.reviewed === true ||
                          selectedVertical?.reviewed === undefined ||
                          countNewItems === 0
                        }
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                        onClick={() => setDialogOpen(true)}
                      >
                        Review New Resources
                      </Button>
                    </span>
                  </Badge>

                  <ReviewNewResourcesModal
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onEdit={editSelectedVertical}
                    vertical_id={vertical_id}
                    items={newItems}
                    setItems={setNewItems}
                    lastRun={displayTime(selectedVertical?.updated_at)}
                  />

                  <VerticalResourceSearchModal
                    open={searchDialogOpen}
                    onClose={() => setSearchDialogOpen(false)}
                    onRunSearch={handleCategoriesSelected}
                    busy={searchDialogBusy}
                  />

                  <RefreshFiltersModal
                    open={filtersDialogOpen}
                    onClose={() => setFiltersDialogOpen(false)}
                    onConfirm={(overrides) => searchNewContent(pendingCategories, overrides)}
                    busy={searchDialogBusy}
                    showFunding={pendingCategories.includes('funding')}
                    showRegion={['organizations', 'jobs', 'events'].some((c) =>
                      pendingCategories.includes(c)
                    )}
                    initialOpportunityStatus={selectedVertical?.OpportunityStatus}
                    initialFundingFilters={selectedVertical?.fundingFilters}
                    initialRegion={selectedVertical?.region}
                  />
                </div>

                {refreshState && (
                  <p style={{ textAlign: 'left', fontWeight: 700, margin: '8px 0 0 0' }}>
                    Number of Refreshes Left: {Math.max(refreshState.limit - refreshState.used, 0)}{' '}
                    of {refreshState.limit} (Resets in {refreshState.days_until_reset} days)
                  </p>
                )}

                <p
                  style={{
                    textAlign: 'left',
                    fontSize: '12px',
                    lineHeight: '1.3',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  All refreshes are account-wide; running a refresh in any vertical counts toward
                  this total.
                  <br /> This process usually completes within a few minutes, but may take up to 15
                  minutes.
                  <br /> You’re free to continue using other parts of the platform while it runs.
                </p>

                {planTier === 'paid' && (
                  <NoMaxWidthTooltip
                    title="Enable automatic weekly searches for new papers, funding, and events, etc."
                    placement="bottom-start"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -20],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedVertical?.auto_refresh}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            toggleAutoRefresh(checked);
                          }}
                        />
                      }
                      label="Auto Refresh Weekly"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontWeight: 600,
                        },
                        paddingTop: '8px',
                      }}
                    />
                  </NoMaxWidthTooltip>
                )}
              </>
            )}

            {(abstract || isEditor) && (
              <div className={styles.abstractCard}>
                <div className={styles.abstractHeader}>
                  <p className={styles.abstractTitle}>📝 Vertical Summary:</p>

                  {isEditor && (
                    <FormDialog
                      title={'Vertical Summary'}
                      content={abstract}
                      onChangeText={setAbstract}
                      description="vertical"
                      _id={selectedVertical._id}
                      type="abstract"
                      category="vertical_abstracts"
                      verticalId={vertical_id}
                      charactersLimit={2500}
                      rows={15}
                    />
                  )}
                </div>

                <div className={styles.abstractBox}>
                  <div className={styles.abstractContent}>
                    {abstract ? (
                      <p className={styles.abstractText}>{abstract}</p>
                    ) : isEditor ? (
                      <p className={styles.abstractPlaceholder}>
                        Add what you think is the most important about this vertical.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.abstractFooter}>
              <p>
                Owner:{' '}
                <Link
                  to={`/vertical/${vertical_id}/users/${selectedVertical?.user_id}`}
                  className={styles.link}
                >
                  {selectedVertical?.user?.name}
                </Link>{' '}
                (
                <a
                  href={`mailto:${selectedVertical?.user?.email}`}
                  target="_blank"
                  className={styles.link}
                >
                  {selectedVertical?.user?.email}
                </a>
                )
              </p>
              <p>
                <Link to={`/vertical/${vertical_id}/people`} className={styles.link}>
                  {totalMembers} {totalMembers > 1 ? 'Members' : 'Member'}
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <Button
          variant="contained"
          disabled={downloading}
          sx={{ textTransform: 'none' }}
          onClick={downloadVerticalCsv}
        >
          {downloading ? 'Downloading...' : 'Download Vertical to CSV'}
        </Button>
      </div>
    </div>
  );
};

export default HomePage;
